/**
 * /terms (OCP-525) - the operator side of Terms versioning: the registry,
 * publishing a new version (text snapshot, effective date, optional change
 * notice), and the accounts that are not on the current version.
 *
 * Publishing with "notify" sends the 13.3 change notice to every account
 * that has not accepted the new text. The effective date must then be in the
 * future: the notice comes BEFORE the effect. No deemed acceptance is
 * implemented anywhere - the list below is what the operator works from.
 *
 * OCP-847 - two documents: the AGB (`agb`) and the data processing agreement
 * under Art. 28 GDPR (`avv`), both accepted with the order click
 * "Kostenpflichtig Buchen". Versions, publishing and the gaps list work per
 * kind; `current` is resolved per kind. Both are mandatory: until an AVV is
 * published, signup, orders, code redemption, free activation and tenant
 * provisioning are refused (`avv_not_published`), as is recording an
 * out-of-band decision.
 *
 * OCP-851 - the service price list (AGB V4 6.6) is managed on this page too
 * (`ServicePriceListSection`): a versioned reference, not an accepted document.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError, readErrorKey } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';
import { ServicePriceListSection } from '../components/ServicePriceListSection';

export type TermsKind = 'agb' | 'avv';

export const TERMS_KIND_LABEL: Record<TermsKind, string> = {
  agb: 'AGB',
  avv: 'AVV (Art. 28 GDPR)',
};

interface TermsVersion {
  id: string;
  /** OCP-847 - absent only on responses from before the kind existed (= agb). */
  kind?: TermsKind;
  version: string;
  title: string;
  language: string;
  content_sha256: string;
  source_doc_id: string | null;
  source_revision: string | null;
  change_summary: string | null;
  effective_from: string;
  objection_deadline: string | null;
  published_at: string;
  published_by: string;
  notices_sent_at: string | null;
  current: boolean;
}

interface Gap {
  customer_id: string;
  email: string;
  company_name: string | null;
  state: 'pending' | 'rejected' | 'none';
  accepted_version: string | null;
  accepted_at: string | null;
  notice_sent_at: string | null;
  /** OCP-657 - the account rejected and the automatic termination is scheduled for this date. */
  termination_ends_at?: string | null;
  notice_mail_id: string | null;
}

function fmt(iso: string | null): string {
  return iso ? fmtDateTime(iso) : '-';
}

export function AdminTermsPage() {
  const [versions, setVersions] = useState<TermsVersion[]>([]);
  const [gaps, setGaps] = useState<{ current_version: TermsVersion | null; items: Gap[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gapFilter, setGapFilter] = useState<'' | 'pending' | 'rejected' | 'none'>('');
  // OCP-847 - which document the versions table and the gaps list show.
  const [kindFilter, setKindFilter] = useState<'' | TermsKind>('');
  const [gapKind, setGapKind] = useState<TermsKind>('agb');

  const load = useCallback(async () => {
    setError(null);
    try {
      const gapQuery = new URLSearchParams({ kind: gapKind, ...(gapFilter ? { state: gapFilter } : {}) });
      const [v, g] = await Promise.all([
        api<{ items: TermsVersion[] }>(`/v1/admin/terms/versions${kindFilter ? `?kind=${kindFilter}` : ''}`),
        api<{ current_version: TermsVersion | null; items: Gap[] }>(`/v1/admin/terms/gaps?${gapQuery.toString()}`),
      ]);
      setVersions(v.items);
      setGaps(g);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load (HTTP ${err.status}).` : 'Could not load.');
    }
  }, [gapFilter, kindFilter, gapKind]);

  const hasAvv = versions.some((v) => v.kind === 'avv');

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-2xl font-semibold tracking-tight">Terms</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Every acceptance binds to an immutable version stored here, including its text. Two documents are
        versioned: the AGB and the AVV (data processing agreement, Art. 28 GDPR); both are accepted with the
        order click &quot;Kostenpflichtig Buchen&quot;. The current version of each is the newest one whose
        effective date has passed; it is what the portal shows at /terms and what every contract
        conclusion is gated on.
      </p>

      {error && (
        <div role="alert" className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <section className="mt-6" data-testid="terms-versions">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Versions</h2>
          <label className="text-sm">
            <span className="mr-2 text-gray-600 dark:text-gray-400">Document</span>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as typeof kindFilter)}
              data-testid="terms-kind-filter"
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
            >
              <option value="">all</option>
              <option value="agb">{TERMS_KIND_LABEL.agb}</option>
              <option value="avv">{TERMS_KIND_LABEL.avv}</option>
            </select>
          </label>
        </div>
        {kindFilter === '' && versions.length > 0 && !hasAvv && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400" data-testid="terms-avv-none">
            No AVV version published. The AVV is mandatory: signup, orders, code redemption and tenant provisioning
            are refused until an AVV is published here.
          </p>
        )}
        {versions.length === 0 ? (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300" data-testid="terms-none">
            No version published. Signup and every contract conclusion are refused until one is.
          </p>
        ) : (
          <table className="mt-2 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-1 pr-3">Document</th>
                <th className="py-1 pr-3">Version</th>
                <th className="py-1 pr-3">Title</th>
                <th className="py-1 pr-3">Effective</th>
                <th className="py-1 pr-3">Objection until</th>
                <th className="py-1 pr-3">Notices</th>
                <th className="py-1 pr-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-t border-gray-100 dark:border-gray-800" data-testid={`terms-version-${v.kind === 'avv' ? 'avv-' : ''}${v.version}`}>
                  <td className="py-1.5 pr-3 text-xs font-semibold">{TERMS_KIND_LABEL[v.kind ?? 'agb']}</td>
                  <td className="py-1.5 pr-3 font-mono">
                    {v.version}
                    {v.current && (
                      <span className="ml-2 rounded bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-green-700 dark:text-green-300">
                        current
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3">{v.title} <span className="text-xs text-gray-500">({v.language})</span></td>
                  <td className="py-1.5 pr-3 tabular-nums">{fmt(v.effective_from)}</td>
                  <td className="py-1.5 pr-3 tabular-nums">{fmt(v.objection_deadline)}</td>
                  <td className="py-1.5 pr-3 tabular-nums">{v.notices_sent_at ? fmt(v.notices_sent_at) : 'not sent'}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-500">{v.source_doc_id ?? '-'}{v.source_revision ? ` @ ${v.source_revision}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <PublishForm onPublished={load} />

      <section className="mt-8" data-testid="terms-gaps">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Accounts not on the current {TERMS_KIND_LABEL[gapKind]} version
            {gaps?.current_version ? ` (${gaps.current_version.version})` : ''}
          </h2>
          <label className="text-sm">
            <span className="mr-2 text-gray-600 dark:text-gray-400">Document</span>
            <select
              value={gapKind}
              onChange={(e) => setGapKind(e.target.value as TermsKind)}
              data-testid="terms-gap-kind"
              className="mr-3 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
            >
              <option value="agb">{TERMS_KIND_LABEL.agb}</option>
              <option value="avv">{TERMS_KIND_LABEL.avv}</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mr-2 text-gray-600 dark:text-gray-400">State</span>
            <select
              value={gapFilter}
              onChange={(e) => setGapFilter(e.target.value as typeof gapFilter)}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
            >
              <option value="">all</option>
              <option value="pending">pending (accepted an older version)</option>
              <option value="rejected">rejected</option>
              <option value="none">none (never decided)</option>
            </select>
          </label>
        </div>
        {gaps && !gaps.current_version && (
          <p className="mt-2 text-sm text-gray-500" data-testid="terms-gaps-unpublished">No {TERMS_KIND_LABEL[gapKind]} version is in force.</p>
        )}
        {gaps && gaps.current_version && gaps.items.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">Every account has accepted the current version.</p>
        )}
        {gaps && gaps.items.length > 0 && (
          <table className="mt-2 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-1 pr-3">Account</th>
                <th className="py-1 pr-3">State</th>
                <th className="py-1 pr-3">Accepted</th>
                <th className="py-1 pr-3">Notice</th>
                <th className="py-1 pr-3">Termination</th>
              </tr>
            </thead>
            <tbody>
              {gaps.items.map((g) => (
                <tr key={g.customer_id} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 pr-3">
                    <Link to={`/accounts/${g.customer_id}`} className="text-brand-600 hover:underline">
                      {g.email}
                    </Link>
                    {g.company_name && <span className="ml-1 text-xs text-gray-500">{g.company_name}</span>}
                  </td>
                  <td className="py-1.5 pr-3">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        g.state === 'rejected'
                          ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                      }`}
                    >
                      {g.state}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-gray-600 dark:text-gray-400">
                    {g.accepted_version ? `${g.accepted_version} on ${fmt(g.accepted_at)}` : '-'}
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-gray-600 dark:text-gray-400">
                    {g.notice_sent_at ? `${fmt(g.notice_sent_at)}${g.notice_mail_id ? '' : ' (send failed)'}` : 'not sent'}
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-red-700 dark:text-red-300" data-testid={`terms-gap-termination-${g.customer_id}`}>{g.termination_ends_at ? `ends ${fmt(g.termination_ends_at)}` : g.state === 'rejected' ? 'rejected, no subscription to end' : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* OCP-851 - the service price list (AGB 6.6): a versioned reference next to the accepted documents. */}
      <ServicePriceListSection />
    </div>
  );
}

const PUBLISH_DEFAULTS: Record<TermsKind, { title: string; sourceDocId: string }> = {
  agb: { title: 'Allgemeine Nutzungsbedingungen SaaS', sourceDocId: 'OWS-D23' },
  avv: { title: 'Auftragsverarbeitungsvertrag (AVV)', sourceDocId: '' },
};

function PublishForm({ onPublished }: { onPublished: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<TermsKind>('agb');
  const [version, setVersion] = useState('');
  const [title, setTitle] = useState(PUBLISH_DEFAULTS.agb.title);
  const [language, setLanguage] = useState('de');
  const [content, setContent] = useState('');
  const [sourceDocId, setSourceDocId] = useState(PUBLISH_DEFAULTS.agb.sourceDocId);
  const [sourceRevision, setSourceRevision] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [notify, setNotify] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api<{ version: TermsVersion; notices: { queued: number } }>('/v1/admin/terms/versions', {
        method: 'POST',
        body: {
          kind,
          version: version.trim(),
          title: title.trim(),
          language: language.trim(),
          content_md: content,
          source_doc_id: sourceDocId.trim() || null,
          source_revision: sourceRevision.trim() || null,
          change_summary: changeSummary.trim() || null,
          ...(effectiveFrom ? { effective_from: new Date(effectiveFrom).toISOString() } : {}),
          notify,
          ...(overrideReason.trim() ? { override_notice_period_reason: overrideReason.trim() } : {}),
        },
      });
      setResult(`Published ${res.version.version} (${TERMS_KIND_LABEL[kind]})${notify ? `, ${res.notices.queued} notice(s) sent` : ''}.`);
      setContent('');
      setVersion('');
      await onPublished();
    } catch (err) {
      const key = err instanceof ApiError ? readErrorKey(err.body) : null;
      setError(
        key === 'version_exists'
          ? 'That version label already exists.'
          : key === 'effective_before_now_with_notice'
            ? 'A notified change needs an effective date in the future - the notice comes before the effect.'
            : key === 'content_required'
              ? 'The text is empty or too short.'
              : err instanceof ApiError
                ? `Publish failed (HTTP ${err.status}).`
                : 'Publish failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm" data-testid="terms-publish">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Publish a version</h2>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          {open ? 'Hide' : 'New version'}
        </button>
      </div>
      {open && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm sm:col-span-2">
            <span className="block text-xs text-gray-500">Document</span>
            <select
              value={kind}
              onChange={(e) => {
                const next = e.target.value as TermsKind;
                setKind(next);
                setTitle(PUBLISH_DEFAULTS[next].title);
                setSourceDocId(PUBLISH_DEFAULTS[next].sourceDocId);
              }}
              data-testid="terms-publish-kind"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
            >
              <option value="agb">{TERMS_KIND_LABEL.agb}</option>
              <option value="avv">{TERMS_KIND_LABEL.avv}</option>
            </select>
            {kind === 'avv' && (
              <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300" data-testid="terms-publish-avv-note">
                Publish only the AVV text counsel has released (Orboto General - Legal). Once an AVV is in force,
                every order and signup records its acceptance next to the AGB, and accounts without it cannot
                conclude a new contract until they accept it.
              </span>
            )}
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500">Version label</span>
            <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2026-10-01" data-testid="terms-publish-version" className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm font-mono" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500">Language</span>
            <input value={language} onChange={(e) => setLanguage(e.target.value)} className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500">Effective from (blank = now)</span>
            <input type="datetime-local" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} data-testid="terms-publish-effective" className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500">Source doc (Orboto legal doc key)</span>
            <input value={sourceDocId} onChange={(e) => setSourceDocId(e.target.value)} className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500">Source revision</span>
            <input value={sourceRevision} onChange={(e) => setSourceRevision(e.target.value)} className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm font-mono" />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="block text-xs text-gray-500">Text (Markdown, snapshotted immutably)</span>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={12} data-testid="terms-publish-content" className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 font-mono text-xs" />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="block text-xs text-gray-500">Change summary (goes into the notice mail)</span>
            <textarea value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} rows={3} className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm" />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="terms-publish-notify" />
            <span>Send the change notice to every account not on this text (requires a future effective date)</span>
          </label>
          {notify && (
            <label className="mt-2 block text-sm">
              <span className="text-xs text-gray-500">Override the AGB 13.3 two-month notice (reason, min 10 chars, audited) - leave empty to enforce it</span>
              <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="terms-publish-override" placeholder="e.g. correction release agreed with counsel" className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm" />
            </label>
          )}
          {error && (
            <div role="alert" className="sm:col-span-2 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
          {result && <p className="sm:col-span-2 text-sm text-green-700 dark:text-green-300" data-testid="terms-publish-result">{result}</p>}
          <div className="sm:col-span-2 flex justify-end">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || !version.trim() || content.length < 100}
              data-testid="terms-publish-submit"
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {busy ? 'Publishing…' : notify ? 'Publish and notify' : 'Publish'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
